import { ImageWithFallback } from "@/components/ImageWithFallback";
import { URLizeString } from "@/functions/capitalizeFirstLetters";
import { generateNavigationUrl } from "@/redux/players/playersSlice";
import { SbcSet, SetVote } from "@/redux/sbcs/sbcSetsSlice";
import { Platform } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { useEffect, useRef, useState } from "react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

import { ProgressBar } from "../ProgressBar/ProgressBar";
import { SetHeaderDetails } from "./SetHeaderDetails";
import { SetHeaderSubtitle } from "./SetHeaderSubtitle";
import { SetHeaderTitle } from "./SetHeaderTitle";
import { SetRewardHeader } from "./SetRewardHeader";
import { SetToggleType, SetToggles } from "./SetToggles";

type Props = {
  sbcSet: SbcSet;
  platform?: Platform;
  onClickSbcToggle: (toggleType: SetToggleType) => void;
  onVote?: (vote: SetVote) => void;
  isOnSbcPage?: boolean;
};

const useMarqueeScroll = (dependency: unknown) => {
  const ref = useRef<HTMLDivElement>(null);
  const [needsScroll, setNeedsScroll] = useState(false);
  const [translateX, setTranslateX] = useState("0px");

  useEffect(() => {
    const el = ref.current;
    if (el && el.parentElement) {
      const parentWidth = el.parentElement.offsetWidth;
      const scrollWidth = el.scrollWidth;
      if (scrollWidth > parentWidth) {
        setNeedsScroll(true);
        setTranslateX(`-${scrollWidth - parentWidth}px`);
      } else {
        setNeedsScroll(false);
        setTranslateX("0px");
      }
    }
  }, [dependency]);

  return { ref, needsScroll, translateX };
};

const MarqueeWrapper = ({
  children,
  refEl,
  needsScroll,
  translateX,
}: {
  children: React.ReactNode;
  refEl: React.RefObject<HTMLDivElement>;
  needsScroll: boolean;
  translateX: string;
}) => (
  <div className="overflow-hidden relative w-full -mt-2 mobile:mt-0">
    <div
      ref={refEl}
      className="inline-block whitespace-nowrap will-change-transform"
      style={
        needsScroll
          ? ({
              animation: "bounce-marquee 6s ease-in-out infinite alternate",
              "--marquee-translate": translateX,
            } as React.CSSProperties)
          : {}
      }
    >
      {children}
    </div>
  </div>
);

export const SetHeader = ({
  sbcSet,
  platform,
  onClickSbcToggle,
  isOnSbcPage,
}: Props) => {
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const navigate = useNavigate();
  const { solvedCount, sbcsCount, isFavorite, isHidden, reward } = sbcSet;

  const onClickPlayerRewards = () => {
    if (sbcSet.reward?.type !== "player") {
      return;
    }
    const navLink = generateNavigationUrl({
      playerRoleId: sbcSet.reward?.metaRating?.playerRoleId,
      resourceId: sbcSet.reward?.resourceId,
      name: sbcSet.name,
    });
    navigate(navLink, {
      state: {
        from: "/sbc-solution/" + URLizeString(sbcSet?.name) + "/" + sbcSet?.id,
        scrollPosition: window.scrollY,
      },
    });
  };

  const isCompleted = solvedCount > 0 && sbcsCount === solvedCount;

  const commonProps = {
    sbcSet,
    platform,
    isCompleted,
    isFavorite,
    isHidden,
    solvedCount,
    sbcsCount,
    reward,
    onClickSbcToggle,
    isOnSbcPage,
    onClickPlayerRewards,
  };

  return isMobile ? (
    <SetHeaderMobile {...commonProps} />
  ) : (
    <SetHeaderDesktop {...commonProps} isOnSbcPage={isOnSbcPage} />
  );
};

type CommonProps = {
  sbcSet: SbcSet;
  platform?: Platform;
  isCompleted: boolean;
  isFavorite: boolean;
  isHidden: boolean;
  solvedCount: number;
  sbcsCount: number;
  reward?: SbcSet["reward"];
  onClickSbcToggle: (toggleType: SetToggleType) => void;
  isOnSbcPage?: boolean;
  onClickPlayerRewards: () => void;
};

const SetHeaderMobile = ({
  sbcSet,
  platform,
  solvedCount,
  sbcsCount,
  onClickPlayerRewards,
}: CommonProps) => {
  const { ref, needsScroll, translateX } = useMarqueeScroll(sbcSet);

  return (
    <div className="flex flex-col py-2">
      <div className="flex flex-row">
        <div className="flex flex-col w-9/12 gap-y-2">
          <div className="flex flex-row -ml-4">
            <ImageWithFallback
              src={sbcSet.imageURL}
              alt="logo"
              className="object-cover w-20 h-20"
              loading="lazy"
              fallback={
                import.meta.env.VITE_APP_ASSETS_BASE_URL +
                "/misc/default-sbc.png"
              }
            />
            <div className="flex flex-col justify-center gap-y-1">
              <SetHeaderTitle
                title={sbcSet.name}
                isNew={sbcSet.isNew}
                hasRefreshed={sbcSet.hasRefreshed}
              />
              <SetHeaderSubtitle {...sbcSet} platform={platform} />
            </div>
          </div>
          <ProgressBar solvedCount={solvedCount} sbcsCount={sbcsCount} />
        </div>
        <div className="w-3/12 p-0 m-0">
          <SetRewardHeader
            reward={sbcSet.reward}
            onlyShowImage
            onClick={onClickPlayerRewards}
          />
        </div>
      </div>
      <MarqueeWrapper
        refEl={ref}
        needsScroll={needsScroll}
        translateX={translateX}
      >
        <SetHeaderDetails {...sbcSet} isOnSbcPage />
      </MarqueeWrapper>
    </div>
  );
};

const SetHeaderDesktop = ({
  sbcSet,
  platform,
  isCompleted,
  isFavorite,
  isHidden,
  solvedCount,
  sbcsCount,
  reward,
  onClickSbcToggle,
  onClickPlayerRewards,
}: CommonProps) => {
  const { ref, needsScroll, translateX } = useMarqueeScroll(sbcSet);

  return (
    <div className="flex flex-wrap lg:flex-nowrap mx-auto gap-4 w-full">
      <div className="flex flex-row flex-1 items-center gap-6 touch-device:min-w-132">
        <ImageWithFallback
          src={sbcSet.imageURL}
          alt="logo"
          className="object-cover w-24 touch-device:w-32"
          fallback={
            import.meta.env.VITE_APP_ASSETS_BASE_URL + "/misc/default-sbc.png"
          }
        />
        <div className="flex flex-col my-auto flex-1 w-full -ml-6">
          <div className="flex justify-between w-full flex-row gap-y-2 items-center">
            <SetHeaderTitle
              title={sbcSet.name}
              isNew={sbcSet.isNew}
              hasRefreshed={sbcSet.hasRefreshed}
            />
            <SetToggles
              onClick={onClickSbcToggle}
              isCompleted={isCompleted}
              isFavorite={isFavorite}
              isHidden={isHidden}
            />
          </div>
          <div className="flex justify-between pt-2 text-white gap-x-6">
            <div className="flex flex-col w-full gap-y-3">
              <SetHeaderSubtitle {...sbcSet} platform={platform} />
              <ProgressBar solvedCount={solvedCount} sbcsCount={sbcsCount} />
              <MarqueeWrapper
                refEl={ref}
                needsScroll={needsScroll}
                translateX={translateX}
              >
                <SetHeaderDetails {...sbcSet} isOnSbcPage />
              </MarqueeWrapper>
            </div>
          </div>
        </div>
      </div>

      {reward && (
        <div className="flex flex-1 my-4 bg-white/5 rounded-md min-w-50">
          <SetRewardHeader
            reward={reward}
            setName={sbcSet.name}
            setDescription={sbcSet.description}
            onClick={onClickPlayerRewards}
          />
        </div>
      )}
    </div>
  );
};
